"use client";

import { useState } from "react";
import { formattaMedia, type Rating } from "@/lib/api";

/**
 * Le stelle.
 *
 * Tre componenti che sembrano uno solo, e vale la pena tenerli distinti perché
 * rispondono a tre domande diverse: *quanto vale* (`Stelle`), *chi lo dice*
 * (`VotoSintetico`) e *quanto valuto io* (`SceltaStelle`).
 *
 * La stella mezza piena si ottiene sovrapponendo due copie della fila e
 * tagliando quella piena con una larghezza in percentuale. È più semplice che
 * disegnare mezze stelle: una sola forma, e la media di 3,7 si vede davvero
 * come 3,7 invece di essere arrotondata a 4 sotto gli occhi di chi legge.
 */

const PUNTA =
  "M12 2.6l2.9 5.9 6.5.9-4.7 4.6 1.1 6.5L12 17.4 6.2 20.5l1.1-6.5L2.6 9.4l6.5-.9z";

function Fila({ classe }: { classe: string }) {
  return (
    <div className={`flex ${classe}`}>
      {[0, 1, 2, 3, 4].map((i) => (
        <svg key={i} viewBox="0 0 24 24" className="size-[1em] shrink-0" aria-hidden>
          <path d={PUNTA} fill="currentColor" />
        </svg>
      ))}
    </div>
  );
}

/** La fila di cinque, riempita per la frazione giusta. */
export function Stelle({
  valore,
  className = "text-base",
}: {
  valore: number;
  className?: string;
}) {
  const percentuale = Math.max(0, Math.min(5, valore)) * 20;
  return (
    <div className={`relative inline-block leading-none ${className}`}>
      <Fila classe="text-line" />
      <div
        className="absolute inset-0 overflow-hidden"
        style={{ width: `${percentuale}%` }}
        aria-hidden
      >
        <Fila classe="text-amber-500" />
      </div>
    </div>
  );
}

/**
 * Il voto come si legge in una scheda: stelle, media, quante.
 *
 * Se le valutazioni comprendono delle dimostrative lo dice, sempre, e non è
 * facoltativo: presentare recensioni inventate come se fossero di clienti è una
 * pratica commerciale vietata in sé. Il dato per dirlo arriva insieme alla
 * media proprio perché non si possa mostrare l'una senza l'altro.
 */
export function VotoSintetico({
  voto,
  className = "",
}: {
  voto: Rating | null | undefined;
  className?: string;
}) {
  const media = formattaMedia(voto);
  if (!voto || media === null) {
    return <span className={`text-xs text-muted ${className}`}>Nessuna valutazione</span>;
  }

  const finte = voto.demo_count > 0;

  return (
    <span className={`inline-flex items-center gap-1.5 ${className}`}>
      <Stelle valore={Number(voto.rating_avg)} className="text-[13px]" />
      <span className="text-xs font-medium">{media}</span>
      <span className="text-xs text-muted">({voto.reviews_count})</span>
      {finte && (
        <span
          title="Valutazioni dimostrative: le abbiamo scritte noi per provare il sito, non sono di clienti."
          className="rounded border border-amber-500/40 bg-amber-500/10 px-1 py-px text-[10px] font-medium uppercase tracking-wide text-amber-700 dark:text-amber-500"
        >
          demo
        </span>
      )}
    </span>
  );
}

/**
 * Da uno a cinque, da scegliere.
 *
 * Nessuna preselezione: partire da cinque stelle è un suggerimento, e un
 * suggerimento sulla valutazione è esattamente ciò che rende una raccolta di
 * recensioni contestabile. Chi vota parte da zero.
 */
export function SceltaStelle({
  valore,
  onCambia,
}: {
  valore: number;
  onCambia: (v: number) => void;
}) {
  const [sopra, setSopra] = useState(0);
  const mostrato = sopra || valore;

  return (
    <div
      className="inline-flex items-center gap-1"
      role="radiogroup"
      aria-label="La tua valutazione, da una a cinque stelle"
      onMouseLeave={() => setSopra(0)}
    >
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          role="radio"
          aria-checked={valore === n}
          aria-label={`${n} ${n === 1 ? "stella" : "stelle"}`}
          onMouseEnter={() => setSopra(n)}
          onFocus={() => setSopra(n)}
          onBlur={() => setSopra(0)}
          onClick={() => onCambia(n)}
          className="rounded p-0.5 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          <svg
            viewBox="0 0 24 24"
            className={`size-7 transition ${
              n <= mostrato ? "text-amber-500" : "text-line hover:text-amber-500/50"
            }`}
            aria-hidden
          >
            <path d={PUNTA} fill="currentColor" />
          </svg>
        </button>
      ))}
      <span className="ml-2 text-sm text-muted">
        {valore ? `${valore} su 5` : "scegli"}
      </span>
    </div>
  );
}
